import { useRef, useEffect, useMemo, useState } from 'react';
import * as echarts from 'echarts';
import {
  PROVINCE_DEFS, CHINA_MAP_NAME_MAP,
  normalizeProvinceKey, toProvinceDisplayName,
} from '../../utils/provinces';
import type { ProvinceSummary } from '../../types';

interface Props {
  provinceSummary: ProvinceSummary[];
  selectedProvince: string | null;
  onProvinceClick: (prov: string | null) => void;
}

export default function ChinaMap({ provinceSummary, selectedProvince, onProvinceClick }: Props) {
  const ref = useRef<HTMLDivElement>(null);
  const chartRef = useRef<echarts.ECharts | null>(null);
  const [mapReady, setMapReady] = useState(false);

  const provinceMap = useMemo(() => {
    const map: Record<string, number> = {};
    provinceSummary.forEach((item) => {
      const key = normalizeProvinceKey(item.province);
      map[key] = (map[key] || 0) + item.highCount;
    });
    return map;
  }, [provinceSummary]);

  useEffect(() => {
    if (!ref.current) return;
    const chart = echarts.init(ref.current);
    chartRef.current = chart;
    chart.showLoading();

    const resize = () => chart.resize();
    const observer = new ResizeObserver(resize);
    observer.observe(ref.current);
    window.addEventListener('resize', resize);

    fetch('/vendor/china-100000_full.json')
      .catch(() => fetch('https://geo.datav.aliyun.com/areas_v3/bound/100000_full.json'))
      .then((res) => res.json())
      .then((json) => {
        echarts.registerMap('china', json);
        chart.hideLoading();
        setMapReady(true);
      })
      .catch(() => { chart.hideLoading(); setMapReady(false); });

    chart.on('click', (params: any) => {
      const short = normalizeProvinceKey(params.name);
      onProvinceClick(short === selectedProvince ? null : short);
    });

    return () => {
      observer.disconnect();
      window.removeEventListener('resize', resize);
      chart.dispose();
    };
  }, []);

  useEffect(() => {
    if (!chartRef.current || !mapReady) return;
    const data = PROVINCE_DEFS.map((prov) => {
      const key = normalizeProvinceKey(prov.key);
      const count = provinceMap[key] || 0;
      return {
        name: toProvinceDisplayName(key),
        value: count,
        highCount: count,
        selected: key === selectedProvince,
      };
    });
    const maxHigh = Math.max(...data.map((d) => d.value), 1);

    chartRef.current.setOption({
      tooltip: {
        trigger: 'item',
        formatter: (params: any) => {
          const enName = toProvinceDisplayName(params.name);
          if (params.data?.highCount > 0)
            return `<div style="font-weight:600">${enName}</div><div style="color:#dc2626">Abnormal-High&nbsp;&nbsp;&nbsp;${params.data.highCount}</div>`;
          return `<div>${enName}</div>`;
        },
      },
      visualMap: {
        min: 0, max: Math.max(maxHigh, 1), left: 'left', bottom: 10,
        text: ['High', 'Low'], inRange: { color: ['#fecaca', '#f87171', '#dc2626', '#991b1b'] },
        calculable: true, show: true, textStyle: { fontSize: 10 }, itemWidth: 12, itemHeight: 80,
      },
      series: [{
        name: 'Abnormal Sales', type: 'map', map: 'china', roam: true, zoom: 1.36,
        layoutCenter: ['50%', '72%'], layoutSize: '94%', selectedMode: 'single',
        nameMap: CHINA_MAP_NAME_MAP,
        emphasis: {
          label: { show: true, fontSize: 10, formatter: (p: any) => toProvinceDisplayName(p.name) },
          itemStyle: { areaColor: '#fbbf24' },
        },
        label: { show: true, fontSize: 9, color: '#475569', formatter: (p: any) => toProvinceDisplayName(p.name) },
        select: { label: { show: true, color: '#111827', fontWeight: 700 }, itemStyle: { borderColor: '#f59e0b', borderWidth: 1.6 } },
        itemStyle: { borderColor: '#cbd5e1', borderWidth: 0.8 },
        data,
      }],
    });
  }, [provinceMap, selectedProvince, mapReady]);

  return <div ref={ref} style={{ width: '100%', height: '100%' }} />;
}
